import {
  Menu,
  ActionIcon,
  Tooltip,
  ScrollArea,
  Collapse,
  Box,
  Text,
  Flex,
  Button,
  Badge,
} from "@mantine/core"
import {
  IconBracketsAngle,
  IconChevronDown,
  IconChevronRight,
  IconZoomIn,
  IconLabel,
} from "@tabler/icons-react"
import { useState, useMemo } from "react"

import { DigestResponses } from "@costory/types/endpoints/digest"

import { ICON_SIZE, ICON_STROKE } from "@costory/shared/const"
import { formatNumber } from "@costory/shared/utils/format"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { getOptions, NodeTree } from "@costory/front/pages/DigestV2/helper"
import { useAxesMapperQuery } from "@costory/front/queries/axes"

export const ButtonAddTo = ({
  addChild,
  total,
  digest,
  node,
  disabled,
  costColumn,
}: {
  addChild: (direction: string) => void
  node?: NodeTree
  total?: number
  digest: DigestResponses.Investigation
  disabled?: boolean
  costColumn: string
}) => {
  // Prepare suggestion options with memoization
  const queryMapper = useAxesMapperQuery()
  const options = useMemo(
    () => getOptions(digest, node?.nodeProps.position || [], costColumn),
    [digest, node, costColumn],
  )
  const suggested = useMemo(
    () =>
      options.filter(
        (elem) =>
          elem.nbrValue > 1 && (total ? total * 0.7 <= elem.totalDiff : true),
      ),
    [options, total],
  )
  const discouraged = useMemo(
    () =>
      options.filter(
        (elem) =>
          !(
            elem.nbrValue > 1 && (total ? total * 0.7 <= elem.totalDiff : true)
          ),
      ),
    [options, total],
  )
  const [suggestedOpened, setSuggestedOpened] = useState(true)
  const [discouragedOpened, setDiscouragedOpened] = useState(true)

  return (
    <QueryWrapper query={queryMapper}>
      {(translateColumnNames) => (
        <Menu
          shadow="md"
          width="max-content"
          radius="sm"
          styles={{
            dropdown: {
              padding: 0,
            },
          }}
          data-intercom-target="costory-tour-digest-add-to-menu"
        >
          {(suggested.length !== 0 || discouraged.length !== 0) &&
            !disabled && (
              <Tooltip
                label="Zoom Into Line"
                data-intercom-target="costory-tour-digest-zoom-tooltip"
              >
                <Menu.Target>
                  <ActionIcon
                    variant="transparent"
                    aria-label="Settings"
                    disabled={disabled}
                    color="gray"
                    data-intercom-target="costory-tour-digest-zoom-into"
                  >
                    <IconZoomIn size={ICON_SIZE} stroke={ICON_STROKE} />
                  </ActionIcon>
                </Menu.Target>
              </Tooltip>
            )}

          <Menu.Dropdown>
            <Button
              display="flex"
              variant="light"
              bg="surfaceHeader"
              style={{
                pointerEvents: "none",
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                justifyContent: "center",
              }}
              pos="sticky"
              top={1}
              h={40}
              data-intercom-target="costory-tour-digest-choose-dimension-header"
            >
              <ActionIcon
                variant="transparent"
                color="gray"
                data-intercom-target="costory-tour-digest-zoom-header-icon"
              >
                <IconZoomIn size={ICON_SIZE} stroke={1.5} />
              </ActionIcon>
              <Text fw={400}>Choose a Dimension for Breakdown</Text>
            </Button>
            <ScrollArea
              style={{
                maxHeight: "450px",
                overflowY: "auto",
                borderBottomLeftRadius: 15,
                borderBottomRightRadius: 15,
              }}
              data-intercom-target="costory-tour-digest-dimensions-scroll-area"
            >
              <Box
                mb={1}
                onClick={() => setSuggestedOpened((o) => !o)}
                data-intercom-target="costory-tour-digest-suggested-dimensions"
              >
                <Flex bg="surfaceHeader" p={4} align="center">
                  <ActionIcon
                    variant="transparent"
                    data-intercom-target="costory-tour-digest-suggested-toggle-icon"
                  >
                    {suggestedOpened ? (
                      <IconChevronDown size={ICON_SIZE} stroke={ICON_STROKE} />
                    ) : (
                      <IconChevronRight size={ICON_SIZE} stroke={ICON_STROKE} />
                    )}
                  </ActionIcon>
                  <Menu.Label
                    style={{
                      fontSize: 14,
                    }}
                  >
                    Suggested
                  </Menu.Label>
                </Flex>
              </Box>
              <Collapse
                in={suggestedOpened}
                data-intercom-target="costory-tour-digest-suggested-collapse"
              >
                {suggested.length > 0 ? (
                  suggested.map((elem) => (
                    <Menu.Item
                      onClick={() => addChild(elem.direction)}
                      key={elem.direction}
                      leftSection={
                        <ActionIcon
                          variant="transparent"
                          bg="surfaceHover"
                          radius="lg"
                          data-intercom-target="costory-tour-digest-suggested-item-icon"
                        >
                          <IconLabel
                            size={ICON_SIZE}
                            style={{ transform: "rotate(220deg)" }}
                          />
                        </ActionIcon>
                      }
                      data-intercom-target="costory-tour-digest-suggested-menu-item"
                    >
                      <Tooltip
                        label={elem.sampleValues.join(", ")}
                        data-intercom-target="costory-tour-digest-suggested-sample-values-tooltip"
                      >
                        <div>
                          <Box display="block" fw={400} c="textPrimary">
                            {translateColumnNames.data.get(elem.direction) ||
                              elem.direction}
                          </Box>
                          <Flex gap={4} align="center">
                            <Badge
                              size="sm"
                              radius="lg"
                              bg="surfaceHover"
                              c="textPrimary"
                              data-intercom-target="costory-tour-digest-suggested-item-count-badge"
                            >
                              {elem.nbrValue}
                            </Badge>
                            <Box c="textPrimary">
                              {formatNumber({ value: elem.totalDiff })}
                            </Box>
                          </Flex>
                        </div>
                      </Tooltip>
                    </Menu.Item>
                  ))
                ) : (
                  <Box
                    px="sm"
                    py="xs"
                    color="dimmed"
                    style={{ fontStyle: "italic" }}
                  >
                    No suggested axes found.
                  </Box>
                )}
              </Collapse>

              <Box
                onClick={() => setDiscouragedOpened((o) => !o)}
                data-intercom-target="costory-tour-digest-other-dimensions"
              >
                <Flex bg="surfaceHeader" p={4} align="center">
                  <ActionIcon
                    variant="transparent"
                    data-intercom-target="costory-tour-digest-other-toggle-icon"
                  >
                    {discouragedOpened ? (
                      <IconChevronDown size={ICON_SIZE} stroke={ICON_STROKE} />
                    ) : (
                      <IconChevronRight size={ICON_SIZE} stroke={ICON_STROKE} />
                    )}
                  </ActionIcon>
                  <Menu.Label
                    style={{
                      fontSize: 14,
                    }}
                  >
                    Other Dimensions
                  </Menu.Label>
                </Flex>
              </Box>

              <Collapse
                in={discouragedOpened}
                data-intercom-target="costory-tour-digest-other-collapse"
              >
                {discouraged.length > 0 ? (
                  discouraged.map((elem) => (
                    <Menu.Item
                      onClick={() => addChild(elem.direction)}
                      key={elem.direction}
                      leftSection={
                        <ActionIcon
                          variant="transparent"
                          bg="surfaceHeader"
                          radius="lg"
                          data-intercom-target="costory-tour-digest-other-item-icon"
                        >
                          <IconBracketsAngle
                            size={ICON_SIZE}
                            stroke={ICON_STROKE}
                          />
                        </ActionIcon>
                      }
                      data-intercom-target="costory-tour-digest-other-menu-item"
                    >
                      <Tooltip
                        label={elem.sampleValues.join(", ")}
                        data-intercom-target="costory-tour-digest-other-sample-values-tooltip"
                      >
                        <div>
                          <Box display="block" fw={400} c="textPrimary">
                            {translateColumnNames.data.get(elem.direction) ||
                              elem.direction}
                          </Box>
                          <Flex gap={4} align="center">
                            <Badge
                              size="sm"
                              radius="lg"
                              bg="surfaceHover"
                              c="textPrimary"
                              data-intercom-target="costory-tour-digest-other-item-count-badge"
                            >
                              {elem.nbrValue}
                            </Badge>
                            <Box c="textPrimary">
                              {formatNumber({ value: elem.totalDiff })}
                            </Box>
                          </Flex>
                        </div>
                      </Tooltip>
                    </Menu.Item>
                  ))
                ) : (
                  <Box
                    px="sm"
                    py="xs"
                    color="dimmed"
                    style={{ fontStyle: "italic" }}
                  >
                    No other axes found.
                  </Box>
                )}
              </Collapse>
            </ScrollArea>
          </Menu.Dropdown>
        </Menu>
      )}
    </QueryWrapper>
  )
}
